import React from 'react';
import { DatePicker } from 'antd';
import { CustomComponentProps } from '../types';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

/**
 * Custom DatePicker wrapper component
 */
export interface MyDatePickerProps extends CustomComponentProps {
  format?: string;
  showTime?: boolean;
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabledDate?: (current: Dayjs) => boolean;
}

const MyDatePicker: React.FC<MyDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  readonly,
  format = 'YYYY-MM-DD',
  showTime = false,
  picker = 'date',
  allowClear = true,
  size = 'middle',
  disabledDate,
  ...rest
}) => {
  const handleChange = (date: Dayjs | null, dateString: string | string[]) => {
    if (onChange) {
      onChange(date);
    }
  };

  return (
    <DatePicker
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled || readonly}
      format={format}
      showTime={showTime}
      picker={picker}
      allowClear={allowClear}
      size={size}
      disabledDate={disabledDate}
      {...rest}
    />
  );
};

/**
 * Custom RangePicker wrapper component
 */
export interface MyRangePickerProps extends CustomComponentProps {
  format?: string;
  showTime?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabledDate?: (current: Dayjs) => boolean;
}

export const MyRangePicker: React.FC<MyRangePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  readonly,
  format = 'YYYY-MM-DD',
  showTime = false,
  allowClear = true,
  size = 'middle',
  disabledDate,
  ...rest
}) => {
  const handleChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
    if (onChange) {
      onChange(dates);
    }
  };

  // Convert placeholder to proper type for RangePicker
  const rangePlaceholder: [string, string] | undefined = Array.isArray(placeholder) && placeholder.length === 2
    ? [String(placeholder[0] || ''), String(placeholder[1] || '')]
    : undefined;

  return (
    <RangePicker
      value={value}
      onChange={handleChange}
      placeholder={rangePlaceholder}
      disabled={disabled || readonly}
      format={format}
      showTime={showTime}
      allowClear={allowClear}
      size={size}
      disabledDate={disabledDate}
      {...rest}
    />
  );
};

export default MyDatePicker;
